import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { LangSwitch } from "@/components/LangSwitch";
import { api, errorText } from "@/lib/api";
import { pick, txt } from "@/lib/format";
import { BilingualFields } from "./Bilingual";
import { ImageUpload } from "./ImageUpload";
import { useAdminLang } from "./context";

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);
const toForm = (m) => ({
  name_en: m?.name_en || "", name_ar: m?.name_ar || "", description_en: m?.description_en || "", description_ar: m?.description_ar || "",
  image_url: m?.image_url || null, logo_url: m?.logo_url || null, slug: m?.slug || "", is_visible: m?.is_visible ?? true,
});

export function MenuEditor({ menu, onClose, onSaved }) {
  const { t, lang, setLang } = useAdminLang();
  const [form, setForm] = useState(() => toForm(menu));
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v, ...(k === "name_en" && !menu?.id && !f.slugTouched ? { slug: slugify(v) } : {}) }));

  const save = async () => {
    if (!form.name_en.trim() && !form.name_ar.trim()) return setErr("val.name");
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(form.slug)) return setErr("mn.slugInvalid");
    setErr(null);
    setSaving(true);
    try {
      const { slugTouched, ...body } = form;
      const r = menu?.id ? await api.put(`/admin/menus/${menu.id}`, body) : await api.post("/admin/menus", body);
      toast.success(t("toast.saved"));
      onSaved(r.data);
    } catch (e) {
      toast.error(errorText(e, t("toast.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="menu-editor">
        <DialogHeader className="flex-row flex-wrap items-center justify-between gap-3 space-y-0 pe-8">
          <DialogTitle>{menu?.id ? t("mn.editTitle") : t("mn.newTitle")}</DialogTitle>
          <LangSwitch lang={lang} onChange={setLang} testid="menu-editor-lang-switch" />
        </DialogHeader>
        {err && <p className="text-xs text-red-400" data-testid="menu-editor-error">{t(err)}</p>}
        <BilingualFields form={form} set={set} t={t} testPrefix="menu" errors={err === "val.name" ? { name: true } : {}} fields={[{ key: "name" }, { key: "description", multiline: true }]} />
        <div className="space-y-1.5">
          <Label htmlFor="menu-slug">{t("mn.slug")}</Label>
          <div className="flex items-center gap-1" dir="ltr">
            <span className="text-sm text-muted-foreground">/menu/</span>
            <Input id="menu-slug" value={form.slug} maxLength={40} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value.toLowerCase(), slugTouched: true }))} data-testid="menu-slug-input" />
          </div>
          <p className="text-[11px] text-muted-foreground">{t("mn.slugHint")}</p>
        </div>
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="space-y-2"><Label>{t("mn.image")}</Label><ImageUpload value={form.image_url} onChange={(v) => set("image_url", v)} t={t} testid="menu-image-upload" aspect="16/10" /></div>
          <div className="space-y-2"><Label>{t("mn.logo")}</Label><div className="max-w-[180px]"><ImageUpload value={form.logo_url} onChange={(v) => set("logo_url", v)} t={t} testid="menu-logo-upload" aspect="1/1" fit="contain" /></div></div>
        </div>
        <label className="flex items-center gap-2 text-sm"><Switch checked={form.is_visible} onCheckedChange={(v) => set("is_visible", v)} data-testid="menu-visible-switch" />{t("mn.visible")}</label>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="menu-cancel-btn">{t("common.cancel")}</Button>
          <Button onClick={save} disabled={saving} data-testid="menu-save-btn">{saving && <Loader2 className="h-4 w-4 animate-spin" />}{saving ? t("common.saving") : t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AssignDialog({ menu, categories, onClose, onSaved }) {
  const { t, lang } = useAdminLang();
  const [picked, setPicked] = useState(() => new Set(categories.filter((c) => c.menu_id === menu.id).map((c) => c.id)));
  const [saving, setSaving] = useState(false);
  const toggle = (id) => setPicked((p) => {
    const n = new Set(p);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    return n;
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/menus/${menu.id}/categories`, { ids: [...picked] });
      toast.success(t("toast.saved"));
      onSaved();
    } catch (e) {
      toast.error(errorText(e, t("toast.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="assign-dialog">
        <DialogHeader className="pe-8"><DialogTitle>{t("mn.assignTitle", { name: pick(menu, "name", lang).text })}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">{t("mn.assignHint")}</p>
        <div className="space-y-2">
          {categories.map((c) => {
            const own = c.menu_id === menu.id;
            const other = !own && (c.menu_name_en || c.menu_name_ar) ? txt({ name_en: c.menu_name_en, name_ar: c.menu_name_ar }, "name", lang) : null;
            return (
              <label key={c.id} className="flex items-center gap-3 rounded-xl border bg-secondary/30 p-3 text-sm" data-testid={`assign-row-${c.id}`}>
                <Checkbox checked={picked.has(c.id)} disabled={own} onCheckedChange={() => toggle(c.id)} data-testid={`assign-check-${c.id}`} />
                <span className="flex-1 font-medium">{txt(c, "name", lang)}</span>
                <span className="text-[11px] text-muted-foreground">{own ? t("mn.inThisMenu") : other ? t("mn.currentlyIn", { name: other }) : t("mn.unassigned")}</span>
              </label>
            );
          })}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={save} disabled={saving} data-testid="assign-save-btn">{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
